import { atom } from 'jotai';
import { atomWithStorage } from 'jotai/utils';
import { TagService } from '../../services/TagService';
import { TaskTag } from '../types/tag';
import { translationEnabledAtom } from './translationAtoms';

// 存储任务标签关联的状态，按任务ID分类
export const taskTagsMapAtom = atomWithStorage<Record<string, TaskTag[]>>('taskTags', {});

// 任务标签加载状态
export const taskTagsLoadingAtom = atom<Record<string, boolean>>({});
export const taskTagsErrorAtom = atom<Record<string, string | null>>({});

// 获取指定任务的标签
export const getTaskTagsAtom = atom(
  null,
  async (get, set, taskId: string) => {
    try {
      // 设置加载状态
      set(taskTagsLoadingAtom, (prev) => ({ ...prev, [taskId]: true }));
      set(taskTagsErrorAtom, (prev) => ({ ...prev, [taskId]: null }));
      
      // 获取任务标签
      const taskTags = await TagService.getTaskTags(taskId);
      
      // 更新状态
      set(taskTagsMapAtom, (prev) => ({ ...prev, [taskId]: taskTags }));
      
      return true;
    } catch (error) {
      console.error(`获取任务${taskId}的标签失败:`, error);
      set(taskTagsErrorAtom, (prev) => ({
        ...prev,
        [taskId]: error instanceof Error ? error.message : '获取任务标签失败',
      }));
      return false;
    } finally {
      set(taskTagsLoadingAtom, (prev) => ({ ...prev, [taskId]: false }));
    }
  }
);

// 为任务添加标签
export const addTagToTaskAtom = atom(
  null,
  async (get, set, { taskId, tagId }: { taskId: string; tagId: string }) => {
    // 检查是否在翻译模式中
    if (get(translationEnabledAtom)) {
      alert('翻译模式下不能编辑内容');
      return false;
    }

    try {
      // 获取当前任务标签
      const currentTaskTags = get(taskTagsMapAtom)[taskId] || [];
      
      // 检查标签是否已添加
      if (currentTaskTags.some(tag => tag.tagId === tagId)) {
        return true;
      }
      
      // 调用API添加标签
      const result = await TagService.addTagToTask(taskId, tagId);
      
      // 重新获取任务标签以确保数据一致性
      await set(getTaskTagsAtom, taskId);
      
      return true;
    } catch (error) {
      console.error(`为任务${taskId}添加标签${tagId}失败:`, error);
      return false;
    }
  }
);

// 从任务移除标签
export const removeTagFromTaskAtom = atom(
  null,
  async (get, set, { taskId, tagId }: { taskId: string; tagId: string }) => {
    // 检查是否在翻译模式中
    if (get(translationEnabledAtom)) {
      alert('翻译模式下不能编辑内容');
      return false;
    }

    try {
      // 乐观更新：先从UI中移除
      const currentTaskTags = get(taskTagsMapAtom)[taskId] || [];
      const updatedTags = currentTaskTags.filter(tag => tag.tagId !== tagId);
      
      set(taskTagsMapAtom, (prev) => ({ ...prev, [taskId]: updatedTags }));
      
      // 调用API移除标签
      await TagService.removeTagFromTask(taskId, tagId);
      
      return true;
    } catch (error) {
      console.error(`从任务${taskId}移除标签${tagId}失败:`, error);
      
      // 失败时回滚UI状态
      await set(getTaskTagsAtom, taskId);
      
      return false;
    }
  }
); 